import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getParasha,
  getTradition,
  clearAllSelections,
  hasCompletedOnboarding,
} from '../utils/userSelections';
import { useTraditionRabbi } from '../utils/rabbiesFirestore';
import { trackNavigation, trackClick } from '../utils/analytics';
import { useIsAdmin } from '../utils/useIsAdmin';
import MoreOptionsSheet from './MoreOptionsSheet';

interface NavItem {
  to: string;
  labelEn: string;
  labelHe: string;
  icon: React.ReactNode;
}

// Custom SVG icons for a cleaner look
const HomeIcon = ({ active }: { active: boolean }) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke={active ? '#C9A857' : 'currentColor'}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path
      d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"
      fill={active ? 'rgba(201, 168, 87, 0.15)' : 'none'}
    />
    <polyline points="9,22 9,12 15,12 15,22" />
  </svg>
);

const BookIcon = ({ active }: { active: boolean }) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke={active ? '#C9A857' : 'currentColor'}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" fill={active ? 'rgba(201, 168, 87, 0.15)' : 'none'} />
    <path
      d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"
      fill={active ? 'rgba(201, 168, 87, 0.15)' : 'none'}
    />
  </svg>
);

const ChartIcon = ({ active }: { active: boolean }) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke={active ? '#C9A857' : 'currentColor'}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 20V10" />
    <path d="M12 20V4" />
    <path d="M6 20v-6" />
  </svg>
);

const RabbiProfileIcon = ({ active }: { active: boolean }) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke={active ? '#C9A857' : 'currentColor'}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9.5 6.5h5v2.5h-5z" />
    <path d="M7 9h10" />
    <circle cx="12" cy="12" r="3.5" fill={active ? 'rgba(201, 168, 87, 0.15)' : 'none'} />
    <path d="M9.5 15.5c0 1.7 1.1 3 2.5 3s2.5-1.3 2.5-3" />
    <path d="M6 21v-1.5A4.5 4.5 0 0 1 10.5 15h3A4.5 4.5 0 0 1 18 19.5V21" />
  </svg>
);

const MoreIcon = ({ active }: { active: boolean }) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill={active ? '#C9A857' : 'currentColor'}
    stroke="none"
  >
    <circle cx="5" cy="12" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="19" cy="12" r="2" />
  </svg>
);

const MORE_ITEM_KEY = '/more-options';

const navItems: NavItem[] = [
  { to: '/my-progress', labelEn: 'Progress', labelHe: 'התקדמות', icon: null },
  { to: '/practice', labelEn: 'Practice', labelHe: 'תרגול', icon: null },
  { to: '/', labelEn: 'Home', labelHe: 'בית', icon: null },
];

const BottomNav: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [showConfirm, setShowConfirm] = useState(false);
  const [loadingReset, setLoadingReset] = useState(false);
  const [isRabbiUser, setIsRabbiUser] = useState(false);
  const [isMoreOptionsOpen, setIsMoreOptionsOpen] = useState(false);
  const isAdmin = useIsAdmin();
  const parasha = getParasha();
  const tradition = getTradition();
  const rabbi = useTraditionRabbi('parasha');
  const completed = hasCompletedOnboarding();
  const homePath = completed ? '/user-portal' : '/user-portal';

  const baseNavItems = useMemo(() => {
    return navItems.map((item) => (item.to === '/' ? { ...item, to: homePath } : item));
  }, [homePath]);

  useEffect(() => {
    // Show Rabbi nav button only after successful rabbi login (session exists).
    // Do not show it on rabbi-login page or based on email match alone.
    const rabbiToken = localStorage.getItem('rabbiToken');
    if (rabbiToken) {
      setIsRabbiUser(true);
      return;
    }
    const rabbiInfoStored = localStorage.getItem('rabbiInfo');
    if (rabbiInfoStored) {
      try {
        const parsed = JSON.parse(rabbiInfoStored);
        if (parsed?.id) {
          setIsRabbiUser(true);
          return;
        }
      } catch {
        // ignore
      }
    }
    setIsRabbiUser(false);
  }, [location.pathname]);

  const dynamicNavItems = useMemo(() => {
    const items = isRabbiUser
      ? [
          ...baseNavItems,
          {
            to: '/rabbi',
            labelEn: 'Rabbi',
            labelHe: 'רב',
            icon: null,
          },
        ]
      : baseNavItems;
    return isAdmin
      ? [
          ...items,
          {
            to: MORE_ITEM_KEY,
            labelEn: 'More',
            labelHe: 'עוד',
            icon: null,
          },
        ]
      : items;
  }, [isRabbiUser, baseNavItems, isAdmin]);

  const handleHomeClick = (e: React.MouseEvent) => {
    e.preventDefault();
    trackClick('Home Nav', 'BottomNav');
    trackNavigation(location.pathname, homePath, 'bottom_nav');
    navigate(homePath);
  };

  const doReset = () => {
    setLoadingReset(true);
    trackClick('Reset Selections', 'BottomNav');
    clearAllSelections();
    setTimeout(() => {
      setLoadingReset(false);
      setShowConfirm(false);
      const isLoggedIn = localStorage.getItem('loggedInUser');
      if (isLoggedIn) {
        trackNavigation(location.pathname, '/aliya-latora', 'reset');
        navigate('/aliya-latora');
        return;
      }
      trackNavigation(location.pathname, '/', 'reset');
      navigate('/');
    }, 200);
  };

  const getIcon = (to: string, active: boolean) => {
    switch (to) {
      case '/':
      case '/user-portal':
        return <HomeIcon active={active} />;
      case '/practice':
        return <BookIcon active={active} />;
      case '/my-progress':
        return <ChartIcon active={active} />;
      case '/rabbi':
        return <RabbiProfileIcon active={active} />;
      case MORE_ITEM_KEY:
        return <MoreIcon active={active} />;
      default:
        return null;
    }
  };

  return (
    <>
      <nav
        style={{
          width: '100%',
          maxWidth: 980,
          background: 'white',
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center',
          direction: 'ltr', // Keep button order consistent regardless of language
          borderTop: '1px solid #E4E7EB',
          boxShadow: '0 -4px 16px rgba(10, 22, 40, 0.06)',
        }}
      >
        {dynamicNavItems.map((item) => {
          const isHome = item.to === '/' || item.to === '/user-portal';
          const isMore = item.to === MORE_ITEM_KEY;
          const active = isMore
            ? isMoreOptionsOpen
            : isHome
              ? location.pathname === '/' ||
                location.pathname === '/user-portal' ||
                location.pathname.startsWith('/user-portal/')
              : location.pathname.startsWith(item.to);
          const itemWidth = `${100 / dynamicNavItems.length}%`;

          const content = (
            <>
              <span
                style={{
                  width: 44,
                  height: 44,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  background: active ? 'rgba(201, 168, 87, 0.12)' : 'transparent',
                  border: active
                    ? '1.5px solid rgba(201, 168, 87, 0.4)'
                    : '1.5px solid transparent',
                  transition: 'all 0.2s ease',
                }}
              >
                {getIcon(item.to, active)}
              </span>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: active ? 600 : 500,
                  marginTop: 2,
                }}
              >
                {i18n.language === 'he' ? item.labelHe : item.labelEn}
              </span>
            </>
          );

          const baseStyles: React.CSSProperties = {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: itemWidth,
            padding: '4px 0',
            color: active ? '#132A49' : '#6B7C93',
            textDecoration: 'none',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            transition: 'color 0.2s ease',
          };

          if (isMore) {
            return (
              <button
                key={item.to}
                onClick={() => {
                  trackClick('More Nav', 'BottomNav');
                  setIsMoreOptionsOpen(true);
                }}
                style={baseStyles}
                aria-label={item.labelEn}
                type="button"
              >
                {content}
              </button>
            );
          }

          if (isHome) {
            return (
              <button
                key={item.to}
                onClick={handleHomeClick}
                style={baseStyles}
                aria-label={item.labelEn}
                type="button"
              >
                {content}
              </button>
            );
          }
          return (
            <Link
              key={item.to}
              to={item.to}
              style={baseStyles}
              aria-label={item.labelEn}
              onClick={() => {
                trackNavigation(location.pathname, item.to, 'bottom_nav');
              }}
            >
              {content}
            </Link>
          );
        })}
      </nav>

      <MoreOptionsSheet isOpen={isMoreOptionsOpen} onClose={() => setIsMoreOptionsOpen(false)} />

      {showConfirm && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 50,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(10, 22, 40, 0.5)',
            backdropFilter: 'blur(8px)',
            padding: 16,
          }}
        >
          <div
            style={{
              background: 'white',
              width: '100%',
              maxWidth: 340,
              borderRadius: 20,
              boxShadow: '0 20px 60px rgba(10, 22, 40, 0.3)',
              padding: 24,
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}
          >
            <h3
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: '#132A49',
                margin: 0,
              }}
            >
              {t('reset_selections')}
            </h3>
            <p
              style={{
                fontSize: 13,
                color: '#6B7C93',
                lineHeight: 1.6,
                margin: 0,
              }}
            >
              {t('already_chosen')}
              <br />
              {parasha && (
                <>
                  <span style={{ fontWeight: 500, color: '#132A49' }}>{t('parasha_label')}:</span>{' '}
                  {i18n.language === 'he' ? parasha.hebrew : parasha.english}
                  <br />
                </>
              )}
              {tradition && (
                <>
                  <span style={{ fontWeight: 500, color: '#132A49' }}>{t('tradition_label')}:</span>{' '}
                  {t(`tradition.${tradition.key}`)}
                  <br />
                </>
              )}
              {rabbi && (
                <>
                  <span style={{ fontWeight: 500, color: '#132A49' }}>{t('rabbi_label')}:</span>{' '}
                  {i18n.language === 'he' ? rabbi.nameHe : rabbi.nameEn}
                  <br />
                </>
              )}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                type="button"
                onClick={doReset}
                disabled={loadingReset}
                style={{
                  width: '100%',
                  borderRadius: 14,
                  padding: '14px 0',
                  fontSize: 'var(--kol-button-font-size, 16px)',
                  fontWeight: 600,
                  background: 'linear-gradient(90deg, #C9A857 0%, #B8924F 100%)',
                  color: '#132A49',
                  border: 'none',
                  cursor: loadingReset ? 'not-allowed' : 'pointer',
                  opacity: loadingReset ? 0.6 : 1,
                  transition: 'all 0.2s ease',
                }}
              >
                {loadingReset ? t('resetting') : t('reset_start_over')}
              </button>
              <button
                type="button"
                onClick={() => {
                  trackClick('Keep Continue', 'BottomNav');
                  setShowConfirm(false);
                  trackNavigation(location.pathname, '/practice', 'modal');
                  navigate('/practice');
                }}
                style={{
                  width: '100%',
                  borderRadius: 14,
                  padding: '14px 0',
                  fontSize: 'var(--kol-button-font-size, 16px)',
                  fontWeight: 600,
                  background: '#132A49',
                  color: 'white',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
              >
                {t('keep_continue')}
              </button>

              <button
                type="button"
                onClick={() => {
                  trackClick('Go Home No Reset', 'BottomNav');
                  setShowConfirm(false);
                  trackNavigation(location.pathname, homePath, 'modal');
                  navigate(homePath);
                }}
                style={{
                  width: '100%',
                  borderRadius: 14,
                  padding: '10px 0',
                  fontSize: 13,
                  fontWeight: 500,
                  background: 'transparent',
                  color: '#6B7C93',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
              >
                {t('go_home_no_reset')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BottomNav;
